/**
 * Step 5 · Statistics & Top-cut.
 * Auto-runs statistics + topcut analysis on entry.
 * P23 (Feb 2026): now mounts the Advanced Drillhole Analytics + Density
 * Telemetry workspaces below the summary cards so every histogram,
 * log-probability, box-plot, contact analysis and Metal-at-Risk chart is
 * visible on this step.
 */
import { useQuery } from "@tanstack/react-query";
import { getStatistics, topCutAnalyze } from "@/lib/api";
import { WizardCard, StatusRow, KV, LoadingBanner } from "@/pages/wizard/_shared";
import AdvancedAnalyticsWorkspace from "@/pages/wizard/AdvancedAnalyticsWorkspace";
import DensityTelemetryPanel from "@/pages/wizard/DensityTelemetryPanel";
import DensityMatrixEditor from "@/pages/wizard/DensityMatrixEditor";
import TopCutReviewPanel from "@/pages/wizard/TopCutReviewPanel";


export default function StatisticsStep({ projectId }) {
  const s = useQuery({
    queryKey: ["wizard-stats-full", projectId],
    queryFn: () => getStatistics(projectId),
    enabled: !!projectId,
    refetchOnMount: "always",
  });
  const tc = useQuery({
    queryKey: ["wizard-topcut", projectId],
    queryFn: () => topCutAnalyze(projectId, {}),
    enabled: !!projectId,
    refetchOnMount: "always",
  });
  if (s.isLoading || tc.isLoading) return <LoadingBanner label="Computing statistics + top-cut…" />;

  const stats = s.data || {};
  const cut = tc.data || {};
  const desc = stats?.summary || stats?.grade_stats || {};

  return (
    <div className="max-w-6xl space-y-4">
      <StatusRow tone="ok">
        Descriptive statistics computed. Recommended top-cut for capping
        outliers: <b>{cut?.recommended_cap?.toFixed?.(2) ?? "—"}</b>
        {" "}(P97.5). Metal-at-risk if applied: <b>{cut?.metal_at_risk_pct?.toFixed?.(2) ?? "—"}%</b>.
      </StatusRow>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <WizardCard title="Grade statistics" testId="w-stats-desc">
          <KV k="Samples"     v={(cut.n_samples || desc.n)?.toLocaleString?.() ?? "—"} />
          <KV k="Mean"        v={fmt(cut.mean ?? desc.mean, 3)} />
          <KV k="Median"      v={fmt(cut.median, 3)} />
          <KV k="Std"         v={fmt(cut.std, 3)} />
          <KV k="CV"          v={fmt(cut.cv, 3)} />
        </WizardCard>

        <WizardCard title="Top-cut analysis" testId="w-stats-topcut">
          <KV k="Recommended cap (P97.5)" v={fmt(cut.recommended_cap, 3)} />
          <KV k="Metal-at-risk %"          v={fmt(cut.metal_at_risk_pct, 2)} />
          <KV k="P90"                       v={fmt(cut.percentiles?.find?.((p) => p.percentile === 90)?.value, 3)} />
          <KV k="P95"                       v={fmt(cut.percentiles?.find?.((p) => p.percentile === 95)?.value, 3)} />
          <KV k="P99"                       v={fmt(cut.percentiles?.find?.((p) => p.percentile === 99)?.value, 3)} />
        </WizardCard>
      </div>

      {/* Sprint P37z · Persistent Top-cut Review — global + per-lithology
                             recommendations from the new backend engine. */}
      <TopCutReviewPanel projectId={projectId} />

      {/* P0.1 · Density Priority Telemetry — every block classified by density source. */}
      <DensityTelemetryPanel projectId={projectId} />

      {/* Sprint P32 · Density Matrix Editor — per-lithology × weathering ×
          domain × oxidation table with AI recommendations + CP sign-off. */}
      <DensityMatrixEditor projectId={projectId} />

      {/* P1.1–P1.6 · Advanced Drillhole Analytics — Histograms, Log-Prob, Box, Contact, MaR. */}
      <AdvancedAnalyticsWorkspace projectId={projectId} />
    </div>
  );
}


function fmt(v, d = 2) {
  if (v == null || !Number.isFinite(v)) return "—";
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: d });
}
